export type SessionTimeSlot = {
  sessionName: string;
  startTime?: string;
  endTime?: string;
};

function timeToMinutes(raw?: string): number {
  const s = String(raw ?? "").trim();
  const m = s.match(/^(\d{1,2}):(\d{2})/);
  if (!m) return NaN;
  const h = parseInt(m[1], 10);
  const min = parseInt(m[2], 10);
  if (!Number.isFinite(h) || !Number.isFinite(min)) return NaN;
  return h * 60 + min;
}

/**
 * Picks the clinic session that matches the current clock time.
 * Inside a session → that session.
 * In a gap (e.g. Morning ends 12:00, Evening starts 14:00) → split the gap
 * in half: first half stays on the previous session, second half moves to the
 * next. Exact midpoint keeps the previous session.
 * Before first / after last → nearest edge session.
 */
export function resolveSessionForCurrentTime(
  sessions: SessionTimeSlot[],
  now: Date = new Date()
): string {
  const nowM = now.getHours() * 60 + now.getMinutes();

  const slots = sessions
    .map((session) => {
      const sessionName = String(session.sessionName ?? "").trim();
      const startM = timeToMinutes(session.startTime);
      const endM = timeToMinutes(session.endTime);
      return { sessionName, startM, endM };
    })
    .filter(
      (slot) =>
        slot.sessionName &&
        Number.isFinite(slot.startM) &&
        Number.isFinite(slot.endM)
    )
    .sort((a, b) => a.startM - b.startM);

  if (slots.length === 0) return "";

  for (const slot of slots) {
    if (nowM >= slot.startM && nowM < slot.endM) {
      return slot.sessionName;
    }
  }

  if (nowM < slots[0].startM) {
    return slots[0].sessionName;
  }

  if (nowM >= slots[slots.length - 1].endM) {
    return slots[slots.length - 1].sessionName;
  }

  for (let i = 0; i < slots.length - 1; i++) {
    const prev = slots[i];
    const next = slots[i + 1];
    if (nowM >= prev.endM && nowM < next.startM) {
      const midpoint = (prev.endM + next.startM) / 2;
      // Prefer previous session on the exact midpoint / first half of the gap.
      return nowM <= midpoint ? prev.sessionName : next.sessionName;
    }
  }

  return slots[slots.length - 1].sessionName;
}

/**
 * Resolve today's default session from General Hours by clock time.
 * When `availableNames` is provided (even empty), only those names may be
 * selected — so a clinic "General" hours label cannot hide Morning/Evening
 * appointment rows. Empty availableNames → "" (All Sessions).
 */
export async function fetchDefaultSessionForToday(
  api: { get: (url: string, config?: { params?: Record<string, string> }) => Promise<{ data?: { data?: { sessions?: SessionTimeSlot[] } } }> },
  dayName: string,
  availableNames?: string[]
): Promise<string> {
  try {
    if (availableNames !== undefined && availableNames.length === 0) {
      return "";
    }
    const response = await api.get(
      "/client/setting/dentistGeneralHours/getGeneralSessionNames",
      { params: { day: dayName } }
    );
    let sessions: SessionTimeSlot[] = response.data?.data?.sessions ?? [];
    if (availableNames !== undefined) {
      const allowed = new Set(
        availableNames.map((name) => String(name).trim().toLowerCase()).filter(Boolean)
      );
      sessions = sessions.filter((session) =>
        allowed.has(String(session.sessionName ?? "").trim().toLowerCase())
      );
    }
    return resolveSessionForCurrentTime(sessions);
  } catch {
    return "";
  }
}
